import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import $ from "jquery";
import "daterangepicker/daterangepicker.css";
import "daterangepicker";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import BotReplyRenderer from "./BotReplyRenderer"; // Import the component

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ChatbotReport = () => {
  const [rowData, setRowData] = useState([]);
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const gridRef = useRef(null);

  useEffect(() => {
    document.title = "Chatbot Report - Calendar Services";
  }, []);

  useEffect(() => {
    $("#kt_daterangepicker_4").daterangepicker(
      {
        startDate: moment(),
        endDate: moment(),
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      (start, end) => {
        setStartDate(start.format("YYYY-MM-DD"));
        setEndDate(end.format("YYYY-MM-DD"));
        $("#kt_daterangepicker_4").html(
          `${start.format("MMMM D, YYYY")} - ${end.format("MMMM D, YYYY")}`
        );
      }
    );
    $("#kt_daterangepicker_4").html(
      `${moment().format("MMMM D, YYYY")} - ${moment().format("MMMM D, YYYY")}`
    );
  }, []);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/chatbot_reports`, {
        startDate,
        endDate,
      });
      setRowData(response.data);
    } catch (error) {
      console.error("Error fetching chatbot report data:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).replace(",", "");
  };

  // Calculate Keyword Frequency
  const keywordFrequency = rowData.reduce((acc, row) => {
    acc[row.keyword] = (acc[row.keyword] || 0) + 1;
    return acc;
  }, {});


  

 // Calculate Keyword Frequency including bot_reply
 const keywordData = rowData.reduce((acc, row) => {
  const keywordWithReply = `${row.keyword}: ${row.bot_reply}`;
  if (!acc[keywordWithReply]) {
    acc[keywordWithReply] = { count: 0, bot_reply: row.bot_reply };
  }
  acc[keywordWithReply].count += 1;
  return acc;
}, {});

const filteredKeywords = Object.entries(keywordData)
  .filter(([_, data]) => data.count > 1)
  .map(([keywordWithReply, data]) => [keywordWithReply, data.count, data.bot_reply]);

// Prepare data for chart with filtered keywords
const keywordFrequencyData = {
  labels: filteredKeywords.map(([keywordWithReply]) => keywordWithReply),
  datasets: [
    {
      label: "Keyword Frequency",
      data: filteredKeywords.map(([_, count]) => count),
      backgroundColor: "rgba(75, 192, 192, 0.6)",
      borderColor: "rgba(75, 192, 192, 1)",
      borderWidth: 1,
    },
  ],
};

  const platformDistribution = rowData.reduce((acc, row) => {
    acc[row.platform] = (acc[row.platform] || 0) + 1;
    return acc;
  }, {});

  const platformDistributionData = {
    labels: Object.keys(platformDistribution),
    datasets: [
      {
        label: "Platform Distribution",
        data: Object.values(platformDistribution),
        backgroundColor: "rgba(153, 102, 255, 0.6)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 1,
      },
    ],
  };

  const columnDefs = [
    { headerName: "S.No", valueGetter: "node.rowIndex + 1", width: 100 },
    { headerName: "Keyword", field: "keyword", width: 200 },
    { headerName: "Bot Reply", field: "bot_reply", width: 300 },
    { headerName: "Platform", field: "platform", width: 150 },
    { headerName: "From App", field: "from_app", width: 150 },
    {
      headerName: "Date",
      field: "cdate",
      width: 209,
      valueFormatter: (params) => formatDate(params.value),
      sortable: true,
    },
  ];

  const KeywordSummary = ({ keywords }) => (
    <div
      className="absolute top-0 right-0 bg-white p-4 rounded-lg shadow-lg text-sm border"
      style={{
        maxWidth: "500px", // Increased width
        maxHeight: "400px", // Increased height
        overflowY: "auto",
        overflowX: "auto",
      }}
    >
      <h4 className="font-bold mb-2 text-lg">Keyword Summary</h4>
      <div style={{ minWidth: "450px" }}> {/* Increased min-width */}
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="border-b">
              <th className="px-4 py-2 text-left border w-2/5">Keyword</th> {/* Adjusted padding */}
              <th className="px-4 py-2 text-left border w-1/5">Count</th> {/* Adjusted padding */}
              <th className="px-4 py-2 text-left border w-2/5">Bot Response</th> {/* Adjusted padding */}
            </tr>
          </thead>
          <tbody>
            {keywords.map(([keyword, count, botReply]) => (
              <tr key={keyword} className="border-b">
                <td className="px-4 py-2 border break-words">{keyword}</td> {/* Adjusted padding */}
                <td className="px-4 py-2 font-bold text-red-500 border">{count}</td> {/* Adjusted padding */}
                <td className="px-4 py-2 border break-words">{botReply}</td> {/* Adjusted padding */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  

  return (
    <div className="max-w-6xl mx-auto mt-8 bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-center text-blue-800">Chatbot Report</h2>

      {/* Date Range Picker */}
      <div className="mb-4 text-center">
        <label className="mr-3 font-bold">Filter by Date:</label>
        <div
          id="kt_daterangepicker_4"
          className="inline-block p-2 border rounded cursor-pointer"
        >
          {`${moment().format("MMMM D, YYYY")} - ${moment().format(
            "MMMM D, YYYY"
          )}`}
        </div>
      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Platform Distribution (Normal Size) */}
        <div className="bg-white p-4 rounded-lg shadow-md ">
          <h3 className="text-xl font-bold mb-4">Platform Distribution</h3>
          <Bar data={platformDistributionData} />
        </div>

        {/* Keyword Frequency (Larger Size) */}
        <div className="bg-white p-6 rounded-lg shadow-md col-span-1 md:col-span-2" style={{ height: "500px", position: "relative" }}>
          <h3 className="text-xl font-bold mb-4">Keyword Frequency</h3>
          <Bar 
            data={keywordFrequencyData} 
            options={{
              maintainAspectRatio: false,
              responsive: true,
              scales: {
                x: {
                  title: {
                    display: true,
                    text: 'Keyword', 
                  },
                  ticks: {
                    display: false, 
                  },
                },
                y: {
                  title: {
                    display: true,
                    text: 'Frequency',
                  },
                },
              },
            }} 
          />
     
        </div>
      </div>

          {/* Keyword Summary Section with Added Margin */}
    {/* Keyword Summary Section with "Read More" */}
<div className="mt-5 mb-6 overflow-auto max-h-64 bg-white-50 p-4 rounded-lg shadow">
  <h4 className="font-bold mb-2 text-lg">Keyword Summary</h4>
  <table className="min-w-full border-collapse">
    <thead>
      <tr className="border-b">
        <th className="px-4 py-2 text-left border">Keyword</th>
        <th className="px-4 py-2 text-left border">Count</th>
        <th className="px-4 py-2 text-left border">Bot Response</th>
      </tr>
    </thead>
    <tbody>
      {filteredKeywords.map(([keyword, count, botReply]) => (
        <tr key={keyword} className="border-b">
          <td className="px-4 py-2 border">{keyword}</td>
          <td className="px-4 py-2 font-bold text-red-500 border">{count}</td>
          <td className="px-4 py-2 border">
            <BotReplyRenderer botReply={botReply} /> {/* Using the new component */}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

      {/* AG Grid Table with Proper Spacing */}
      <div className="ag-theme-alpine" style={{ height: "550px", width: "100%" }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          pagination={true}
          paginationPageSize={15}
          defaultColDef={{
            sortable: true,
            resizable: true,
            editable: true,
            filter: true,
          }}
        />
      </div>

    </div>
  );
};

export default ChatbotReport;